/*
Copyright 2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type ChangeEvent, type FC, type TouchEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@vector-im/compound-web";
import {
  ChevronRightIcon,
  GridIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import classNames from "classnames";

import styles from "./ScreenShareViewToggle.module.css";

export type ScreenShareView = "carousel" | "grid";

interface Props {
  view: ScreenShareView;
  setView: (view: ScreenShareView) => void;
  className?: string;
  onTouchEnd?: (e: TouchEvent) => void;
}

export const ScreenShareViewToggle: FC<Props> = ({
  view,
  setView,
  className,
  onTouchEnd,
}) => {
  const { t } = useTranslation();

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newView = e.target.value as ScreenShareView;
      // Debug logging
      console.log("[ScreenShareViewToggle] Changing view from", view, "to", newView);
      setView(newView);
    },
    [setView, view],
  );

  return (
    <div className={classNames(styles.toggle, className)}>
      <Tooltip label={t("screen_share_view_carousel_label")}>
        <input
          type="radio"
          name="screenshare-view"
          value="carousel"
          checked={view === "carousel"}
          onChange={onChange}
          onTouchEnd={onTouchEnd}
        />
      </Tooltip>
      <ChevronRightIcon aria-hidden width={24} height={24} />
      <Tooltip label={t("screen_share_view_grid_label")}>
        <input
          type="radio"
          name="screenshare-view"
          value="grid"
          checked={view === "grid"}
          onChange={onChange}
          onTouchEnd={onTouchEnd}
        />
      </Tooltip>
      <GridIcon aria-hidden width={24} height={24} />
    </div>
  );
};
